/**
 * Maps API preference types/questions into questionnaireMap.
 *
 * Belongs here: API → view model transforms.
 * Does not belong here: display formatting.
 */
import type {
  PreferenceListApiResponse,
  PreferenceQuestionnaireDataResponse,
  PreferenceQuestionGroup,
  FundPreferenceType,
  PreferencesQuestionnairePreferenceTypeApiItem
} from '../preferences/setup/types'
import type { Question } from '../shared/types/questionnaire.types'
import { mapApiQuestionToQuestion } from './mapApiQuestionToQuestion'
import { PreferenceQuestionType } from '../preferences/setup/types'

export type PreferencesQuestionnaireMap = Record<string, { questionIds: string[]; questions: Question[] }>

/** True when questionType is SEGMENT_INTRO (insight screen). */
export function isSegmentIntroQuestion(question: {
  questionType?: string | null
} | null | undefined): boolean {
  return question?.questionType === PreferenceQuestionType.SEGMENT_INTRO
}

export function mapPreferenceListToFundPreferenceTypes(list: PreferenceListApiResponse): FundPreferenceType[] {
  const preferenceList = list?.preferenceList ?? []
  return preferenceList.map((item) => ({
    id: item?.id ?? '',
    name: item?.title ?? '',
    fundCount: item?.fundList?.length ?? 0,
    portfolioPercent: item?.allocationPercent,
    questionIds: [],
    fundList: item?.fundList ?? undefined,
    disclaimer: item?.disclaimer ?? undefined
  }))
}

/** Maps one preference list item from details API into FundPreferenceType. */
export function mapPreferenceApiItemToFundPreferenceType(
  preference: PreferencesQuestionnairePreferenceTypeApiItem
): FundPreferenceType {
  const fundList = preference?.fundList ?? []
  return {
    id: preference.id,
    name: preference.title,
    fundCount: preference?.fundCount ?? 0,
    isNewPreference: preference?.isNewPreference === true ? true : undefined,
    portfolioPercent: preference?.portfolioShare ?? undefined,
    questionIds: [],
    fundList,
    disclaimer: preference?.disclaimer ?? undefined,
    groupName: preference?.groupName ?? undefined,
    groups: preference?.groups ?? undefined
  }
}

export function buildQuestionnaireMap(data: PreferenceQuestionnaireDataResponse): PreferencesQuestionnaireMap {
  const groups: PreferenceQuestionGroup[] = data?.questionGroups ?? []
  const map: PreferencesQuestionnaireMap = {}
  for (const group of groups) {
    const preferenceTypeId = group?.groupId ?? ''
    if (!preferenceTypeId) continue
    const questions = (group.questions ?? []).map(mapApiQuestionToQuestion)
    const questionIds = questions.map((question) => question?.id).filter((id): id is string => (id ?? '') !== '')
    map[preferenceTypeId] = { questionIds, questions }
  }
  return map
}
